import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import { fmtINR, fmtDateDMY, todayISO } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Plus, Search, Lock, FileText } from "lucide-react";

export default function ShiftEntries() {
  const [entries, setEntries] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/shifts/");
        setEntries(r.data || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = entries.filter((e) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (
      (e.cashier_name || "").toLowerCase().includes(s) ||
      (e.attendee_names || []).join(",").toLowerCase().includes(s) ||
      (e.date || "").includes(s) ||
      (e.shift_code || "").toLowerCase().includes(s)
    );
  });

  // group by date desc
  const groups = {};
  for (const e of filtered) {
    groups[e.date] = groups[e.date] || [];
    groups[e.date].push(e);
  }
  const dates = Object.keys(groups).sort((a, b) => b.localeCompare(a));

  return (
    <div className="space-y-4" data-testid="shifts-list-root">
      <div className="flex items-end justify-between gap-2 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Shifts</h1>
          <p className="text-sm text-slate-600 mt-1">All shift entries. Newest on top.</p>
        </div>
        <Link to="/shifts/new">
          <Button data-testid="add-shift-btn" className="bg-slate-900 hover:bg-slate-800 text-white">
            <Plus size={16} className="mr-1" /> Add
          </Button>
        </Link>
      </div>

      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          data-testid="shifts-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by cashier, filler, date, shift…"
          className="pl-9 h-11 border-slate-300"
        />
      </div>

      {loading && <div className="text-sm text-slate-500">Loading…</div>}
      {!loading && dates.length === 0 && (
        <Card className="p-8 text-center border-2 border-slate-300 border-dashed rounded-sm">
          <p className="text-slate-600">No entries yet. Click <strong>Add</strong> to create the first shift entry.</p>
        </Card>
      )}

      {dates.map((d) => (
        <div key={d}>
          <div className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-2 mt-4">{fmtDateDMY(d)}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {groups[d].map((e) => (
              <Link
                key={e.entry_id}
                to={`/shifts/${e.entry_id}`}
                data-testid={`shift-card-${e.entry_id}`}
                className="block bg-white border-2 border-slate-300 rounded-sm p-3 hover:border-slate-900 transition-colors"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-bold text-slate-900 truncate">{(e.attendee_names || []).join(", ") || "—"}</div>
                    <div className="text-xs text-slate-600 mt-1">
                      <span className="inline-block px-2 py-0.5 bg-slate-100 border border-slate-300 rounded-sm mr-2 font-bold">{e.shift_code}</span>
                      Cashier: <strong>{e.cashier_name || "—"}</strong>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {e.status === "LOCKED" && <Lock size={14} className="text-slate-500" />}
                    <span className={
                      "text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border " +
                      (e.status === "DRAFT" ? "state-warn" : e.status === "SUBMITTED" ? "state-info" : "state-ok")
                    }>{e.status}</span>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                  <div>
                    <div className="text-slate-500">Fuels</div>
                    <div className="num font-bold">{fmtINR(e.total_sales_fuels)}</div>
                  </div>
                  <div>
                    <div className="text-slate-500">CNG</div>
                    <div className="num font-bold">{fmtINR(e.total_sales_cng)}</div>
                  </div>
                  <div>
                    <div className="text-slate-500">Diff</div>
                    <div className={"num font-bold " + (Math.abs((e.difference_fuels || 0) + (e.difference_cng || 0)) > 10 ? "text-red-600" : "text-emerald-700")}>
                      {fmtINR((e.difference_fuels || 0) + (e.difference_cng || 0))}
                    </div>
                  </div>
                </div>
                {/* One-tap end-of-shift PDF — kills the 6 pm phone call. */}
                <div className="mt-3 pt-2 border-t border-slate-200 flex items-center justify-between gap-2">
                  <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Handover to manager</span>
                  <a
                    href={`${API_BASE}/shifts/${e.entry_id}/summary_pdf`}
                    target="_blank" rel="noreferrer"
                    onClick={(ev) => ev.stopPropagation()}
                    className="text-xs font-bold px-2 py-1 bg-slate-900 text-white rounded-sm hover:bg-slate-800 inline-flex items-center gap-1"
                    data-testid={`summary-pdf-${e.entry_id}`}
                  >
                    <FileText size={12} /> Summary PDF
                  </a>
                </div>
              </Link>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
